/**
 * MINIMOG / ZEST — CART SECTION GUARD
 * -------------------------------------------------------------------------
 * The theme replaces the drawer by parsing the re-rendered `cart-drawer`
 * section and reading three containers out of it:
 *
 *   getSectionInnerHTML(html, selector) {
 *     return new DOMParser().parseFromString(html, 'text/html')
 *              .querySelector(selector).innerHTML;   // <- throws on null
 *   }
 *
 * If any one selector is missing from the returned HTML, that throw aborts
 * the whole forEach, so NONE of the sections update — add to cart, remove and
 * quantity changes all appear to do nothing until the page is reloaded.
 *
 * This guard replaces the two update methods with null-safe versions that do
 * exactly what the theme does, except a missing container is skipped with a
 * console warning instead of taking the entire cart down.
 *
 * Set window.CartDrawerProDisableGuard = true before this file loads to
 * opt out entirely.
 * -------------------------------------------------------------------------
 */
(function () {
  'use strict';

  if (window.CartDrawerProDisableGuard) return;

  function warn() {
    var args = Array.prototype.slice.call(arguments);
    console.warn.apply(console, ['[Cart Section Guard]'].concat(args));
  }

  function applySections(instance, html, source) {
    if (typeof html !== 'string' || html.length === 0) {
      warn('no HTML returned for section "' + instance.sectionName + '" (' + source + '). Nothing updated.');
      return false;
    }

    var doc = new DOMParser().parseFromString(html, 'text/html');
    var applied = 0;

    instance.getSectionsToRender().forEach(function (section) {
      var root = document.getElementById(section.id);
      if (!root) return; // container not on this page (e.g. cart page ids)

      var target = root.querySelector(section.selector) || root;
      var incoming = doc.querySelector(section.selector);

      if (!incoming) {
        // Only worth reporting when the live page actually has this container.
        if (root.querySelector(section.selector)) {
          warn(section.selector + ' is missing from the re-rendered section — skipped.');
        }
        return;
      }

      target.innerHTML = incoming.innerHTML;
      applied++;
    });

    return applied > 0;
  }

  function patch() {
    var ctor = customElements.get('m-cart-items');
    if (!ctor) return false;

    var proto = ctor.prototype;
    if (typeof proto.renderCartSections === 'function') return true;
    if (proto.__cartSectionGuarded) return true;
    proto.__cartSectionGuarded = true;

    /* ------------------------------------------------- quantity / remove */
    proto.onCartUpdate = function (event) {
      var cart = event && event.cart;
      var html = cart && cart.sections ? cart.sections[this.sectionName] : null;

      applySections(this, html, 'cart:update');

      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
    };

    /* --------------------------------------------------- add to cart etc */
    proto.onCartRefresh = function (event) {
      var self = this;
      var cartSelector = this.isCartPage ? 'm-cart' : 'm-cart-drawer';
      var cartElement = document.querySelector(cartSelector);
      var cartCount = document.querySelectorAll('m-cart-count');

      return fetch(this.rootUrl + '?sections[]=' + this.sectionName + '&sections[]=' + this.cartCountSectionName)
        .then(function (response) {
          return response.text();
        })
        .then(function (responseText) {
          var parsed = JSON.parse(responseText);

          applySections(self, parsed[self.sectionName], 'cart:refresh');

          var countHTML = parsed[self.cartCountSectionName];
          if (typeof countHTML === 'string') {
            var newCartCount = new DOMParser().parseFromString(countHTML, 'text/html');
            cartCount.forEach(function (el) {
              if (typeof el.onUpdate === 'function') el.onUpdate(newCartCount);
            });
          }
        })
        .then(function () {
          // Opening the drawer must not depend on the render succeeding.
          if (event && event.detail && event.detail.open === true && !self.isCartPage && cartElement) {
            cartElement.open();
          }
        })
        .catch(function (error) {
          warn('refresh failed:', error);
          if (event && event.detail && event.detail.open === true && !self.isCartPage && cartElement) {
            cartElement.open();
          }
        });
    };

    return true;
  }

  if (!patch()) {
    if (window.customElements && customElements.whenDefined) {
      customElements.whenDefined('m-cart-items').then(patch);
    } else {
      document.addEventListener('DOMContentLoaded', patch);
    }
  }
})();

